import { Injectable, signal } from '@angular/core';

const DATE_FORMAT_KEY = 'dateFormat';
const TIMEZONE_KEY = 'dateTimezone';

export const DEFAULT_DATE_FORMAT = 'yyyy/MM/dd HH:mm:ss';
export const DEFAULT_TIMEZONE = ''; // empty string = browser local time

export interface DateFormatOption {
  label: string;
  value: string;
}

export interface TimezoneOption {
  label: string;
  value: string;
}

export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  // ── Date + time (24-hour) ─────────────────────────────────────────────────
  { label: 'yyyy/MM/dd HH:mm:ss  (e.g. 2025/03/04 14:30:00)', value: 'yyyy/MM/dd HH:mm:ss' },
  { label: 'MM/dd/yyyy HH:mm:ss  (e.g. 03/04/2025 14:30:00)', value: 'MM/dd/yyyy HH:mm:ss' },
  { label: 'dd/MM/yyyy HH:mm:ss  (e.g. 04/03/2025 14:30:00)', value: 'dd/MM/yyyy HH:mm:ss' },
  { label: 'yyyy-MM-dd HH:mm:ss  (e.g. 2025-03-04 14:30:00)', value: 'yyyy-MM-dd HH:mm:ss' },
  { label: 'dd MMM yyyy HH:mm:ss (e.g. 04 Mar 2025 14:30:00)', value: 'dd MMM yyyy HH:mm:ss' },
  { label: 'MMM dd, yyyy HH:mm:ss (e.g. Mar 04, 2025 14:30:00)', value: 'MMM dd, yyyy HH:mm:ss' },
  // ── Date + time (12-hour) ─────────────────────────────────────────────────
  { label: 'MM/dd/yyyy hh:mm a   (e.g. 03/04/2025 02:30 PM)', value: 'MM/dd/yyyy hh:mm a' },
  { label: 'dd/MM/yyyy hh:mm a   (e.g. 04/03/2025 02:30 PM)', value: 'dd/MM/yyyy hh:mm a' },
  { label: 'dd MMM yyyy hh:mm a  (e.g. 04 Mar 2025 02:30 PM)', value: 'dd MMM yyyy hh:mm a' },
  { label: 'MMM dd, yyyy hh:mm a (e.g. Mar 04, 2025 02:30 PM)', value: 'MMM dd, yyyy hh:mm a' },
  // ── Date + time (no seconds) ──────────────────────────────────────────────
  { label: 'dd/MM/yyyy HH:mm     (e.g. 04/03/2025 14:30)',    value: 'dd/MM/yyyy HH:mm' },
  { label: 'yyyy-MM-dd HH:mm     (e.g. 2025-03-04 14:30)',    value: 'yyyy-MM-dd HH:mm' },
  { label: 'dd MMM yyyy HH:mm    (e.g. 04 Mar 2025 14:30)',   value: 'dd MMM yyyy HH:mm' },
  // ── Date only ─────────────────────────────────────────────────────────────
  { label: 'yyyy/MM/dd           (e.g. 2025/03/04)',           value: 'yyyy/MM/dd' },
  { label: 'MM/dd/yyyy           (e.g. 03/04/2025)',           value: 'MM/dd/yyyy' },
  { label: 'dd/MM/yyyy           (e.g. 04/03/2025)',           value: 'dd/MM/yyyy' },
  { label: 'yyyy-MM-dd           (e.g. 2025-03-04)',           value: 'yyyy-MM-dd' },
  { label: 'dd MMM yyyy          (e.g. 04 Mar 2025)',          value: 'dd MMM yyyy' },
  { label: 'MMM dd, yyyy         (e.g. Mar 04, 2025)',         value: 'MMM dd, yyyy' },
  { label: 'MMMM dd, yyyy        (e.g. March 04, 2025)',       value: 'MMMM dd, yyyy' },
  { label: 'dd MMMM yyyy         (e.g. 04 March 2025)',        value: 'dd MMMM yyyy' },
];

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { label: 'UTC',                                                    value: 'UTC' },
  { label: 'UTC-12:00  Baker Island',                                value: 'Etc/GMT+12' },
  { label: 'UTC-11:00  Pago Pago, Niue',                            value: 'Pacific/Pago_Pago' },
  { label: 'UTC-10:00  Honolulu, Tahiti, Rarotonga',                value: 'Pacific/Honolulu' },
  { label: 'UTC-09:30  Marquesas Islands',                          value: 'Pacific/Marquesas' },
  { label: 'UTC-09:00  Anchorage, Juneau (Alaska)',                  value: 'America/Anchorage' },
  { label: 'UTC-08:00  Los Angeles, Vancouver, Tijuana',            value: 'America/Los_Angeles' },
  { label: 'UTC-07:00  Denver, Phoenix, Edmonton, Chihuahua',       value: 'America/Denver' },
  { label: 'UTC-06:00  Chicago, Mexico City, Guatemala, Winnipeg',  value: 'America/Chicago' },
  { label: 'UTC-05:00  New York, Toronto, Bogotá, Lima, Panama',    value: 'America/New_York' },
  { label: 'UTC-04:00  Halifax, Caracas, La Paz, Santiago, Manaus', value: 'America/Halifax' },
  { label: 'UTC-03:30  St. John\'s (Newfoundland)',                  value: 'America/St_Johns' },
  { label: 'UTC-03:00  São Paulo, Buenos Aires, Montevideo, Nuuk',  value: 'America/Sao_Paulo' },
  { label: 'UTC-02:00  South Georgia, Fernando de Noronha',         value: 'Atlantic/South_Georgia' },
  { label: 'UTC-01:00  Cape Verde, Azores',                         value: 'Atlantic/Cape_Verde' },
  { label: 'UTC+00:00  London, Dublin, Lisbon, Reykjavik, Accra',   value: 'Europe/London' },
  { label: 'UTC+01:00  Paris, Berlin, Madrid, Rome, Lagos, Warsaw', value: 'Europe/Paris' },
  { label: 'UTC+02:00  Helsinki, Kyiv, Cairo, Johannesburg, Beirut',value: 'Europe/Helsinki' },
  { label: 'UTC+03:00  Moscow, Riyadh, Istanbul, Nairobi, Baghdad', value: 'Europe/Moscow' },
  { label: 'UTC+03:30  Tehran (Iran)',                               value: 'Asia/Tehran' },
  { label: 'UTC+04:00  Dubai, Baku, Yerevan, Tbilisi, Muscat',      value: 'Asia/Dubai' },
  { label: 'UTC+04:30  Kabul (Afghanistan)',                         value: 'Asia/Kabul' },
  { label: 'UTC+05:00  Karachi, Tashkent, Yekaterinburg',           value: 'Asia/Karachi' },
  { label: 'UTC+05:30  Mumbai, New Delhi, Colombo',                 value: 'Asia/Kolkata' },
  { label: 'UTC+05:45  Kathmandu (Nepal)',                          value: 'Asia/Kathmandu' },
  { label: 'UTC+06:00  Dhaka, Almaty, Bishkek, Thimphu',           value: 'Asia/Dhaka' },
  { label: 'UTC+06:30  Yangon (Myanmar)',                           value: 'Asia/Yangon' },
  { label: 'UTC+07:00  Bangkok, Hanoi, Jakarta, Novosibirsk',       value: 'Asia/Bangkok' },
  { label: 'UTC+08:00  Beijing, Singapore, Hong Kong, Perth, Manila',value: 'Asia/Shanghai' },
  { label: 'UTC+08:45  Eucla (Western Australia)',                  value: 'Australia/Eucla' },
  { label: 'UTC+09:00  Tokyo, Seoul, Jayapura, Yakutsk',            value: 'Asia/Tokyo' },
  { label: 'UTC+09:30  Adelaide, Darwin',                           value: 'Australia/Adelaide' },
  { label: 'UTC+10:00  Sydney, Melbourne, Brisbane, Vladivostok',   value: 'Australia/Sydney' },
  { label: 'UTC+10:30  Lord Howe Island',                           value: 'Australia/Lord_Howe' },
  { label: 'UTC+11:00  Noumea, Honiara, Magadan',                   value: 'Pacific/Noumea' },
  { label: 'UTC+12:00  Auckland, Wellington, Suva, Fiji',           value: 'Pacific/Auckland' },
  { label: 'UTC+12:45  Chatham Islands (New Zealand)',              value: 'Pacific/Chatham' },
  { label: 'UTC+13:00  Nuku\'alofa, Apia, Tokelau',                 value: 'Pacific/Tongatapu' },
  { label: 'UTC+14:00  Kiritimati (Line Islands)',                  value: 'Pacific/Kiritimati' },
];

/**
 * Timezone options using Windows-style display names, with IANA values for date-fns.
 * The backend (.NET) sends Windows timezone names; these labels match those names.
 */
export const WINDOWS_TIMEZONE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Dateline Standard Time',          value: 'Etc/GMT+12' },
  { label: 'UTC-11',                           value: 'Pacific/Pago_Pago' },
  { label: 'Hawaiian Standard Time',           value: 'Pacific/Honolulu' },
  { label: 'Marquesas Standard Time',          value: 'Pacific/Marquesas' },
  { label: 'Alaskan Standard Time',            value: 'America/Anchorage' },
  { label: 'Pacific Standard Time',            value: 'America/Los_Angeles' },
  { label: 'US Mountain Standard Time',        value: 'America/Phoenix' },
  { label: 'Mountain Standard Time',           value: 'America/Denver' },
  { label: 'Central America Standard Time',    value: 'America/Guatemala' },
  { label: 'Central Standard Time',            value: 'America/Chicago' },
  { label: 'Eastern Standard Time',            value: 'America/New_York' },
  { label: 'US Eastern Standard Time',         value: 'America/Indiana/Indianapolis' },
  { label: 'Venezuela Standard Time',          value: 'America/Caracas' },
  { label: 'Atlantic Standard Time',           value: 'America/Halifax' },
  { label: 'Newfoundland Standard Time',       value: 'America/St_Johns' },
  { label: 'E. South America Standard Time',   value: 'America/Sao_Paulo' },
  { label: 'Argentina Standard Time',          value: 'America/Argentina/Buenos_Aires' },
  { label: 'Montevideo Standard Time',         value: 'America/Montevideo' },
  { label: 'SA Eastern Standard Time',         value: 'America/Cayenne' },
  { label: 'UTC-02',                           value: 'Etc/GMT+2' },
  { label: 'Azores Standard Time',             value: 'Atlantic/Azores' },
  { label: 'Cape Verde Standard Time',         value: 'Atlantic/Cape_Verde' },
  { label: 'UTC',                              value: 'UTC' },
  { label: 'GMT Standard Time',                value: 'Europe/London' },
  { label: 'Greenwich Standard Time',          value: 'Atlantic/Reykjavik' },
  { label: 'W. Europe Standard Time',          value: 'Europe/Berlin' },
  { label: 'Central Europe Standard Time',     value: 'Europe/Budapest' },
  { label: 'Romance Standard Time',            value: 'Europe/Paris' },
  { label: 'W. Central Africa Standard Time',  value: 'Africa/Lagos' },
  { label: 'South Africa Standard Time',       value: 'Africa/Johannesburg' },
  { label: 'GTB Standard Time',                value: 'Europe/Bucharest' },
  { label: 'Middle East Standard Time',        value: 'Asia/Beirut' },
  { label: 'Egypt Standard Time',              value: 'Africa/Cairo' },
  { label: 'FLE Standard Time',                value: 'Europe/Kyiv' },
  { label: 'Israel Standard Time',             value: 'Asia/Jerusalem' },
  { label: 'Libya Standard Time',              value: 'Africa/Tripoli' },
  { label: 'Namibia Standard Time',            value: 'Africa/Windhoek' },
  { label: 'Arabic Standard Time',             value: 'Asia/Baghdad' },
  { label: 'Turkey Standard Time',             value: 'Europe/Istanbul' },
  { label: 'Arab Standard Time',               value: 'Asia/Riyadh' },
  { label: 'Russian Standard Time',            value: 'Europe/Moscow' },
  { label: 'E. Africa Standard Time',          value: 'Africa/Nairobi' },
  { label: 'Iran Standard Time',               value: 'Asia/Tehran' },
  { label: 'Arabian Standard Time',            value: 'Asia/Dubai' },
  { label: 'Azerbaijan Standard Time',         value: 'Asia/Baku' },
  { label: 'Georgian Standard Time',           value: 'Asia/Tbilisi' },
  { label: 'Caucasus Standard Time',           value: 'Asia/Yerevan' },
  { label: 'Afghanistan Standard Time',        value: 'Asia/Kabul' },
  { label: 'Pakistan Standard Time',           value: 'Asia/Karachi' },
  { label: 'West Asia Standard Time',          value: 'Asia/Tashkent' },
  { label: 'India Standard Time',              value: 'Asia/Kolkata' },
  { label: 'Sri Lanka Standard Time',          value: 'Asia/Colombo' },
  { label: 'Nepal Standard Time',              value: 'Asia/Kathmandu' },
  { label: 'Central Asia Standard Time',       value: 'Asia/Almaty' },
  { label: 'Bangladesh Standard Time',         value: 'Asia/Dhaka' },
  { label: 'Myanmar Standard Time',            value: 'Asia/Yangon' },
  { label: 'SE Asia Standard Time',            value: 'Asia/Bangkok' },
  { label: 'China Standard Time',              value: 'Asia/Shanghai' },
  { label: 'Singapore Standard Time',          value: 'Asia/Singapore' },
  { label: 'W. Australia Standard Time',       value: 'Australia/Perth' },
  { label: 'Taipei Standard Time',             value: 'Asia/Taipei' },
  { label: 'Tokyo Standard Time',              value: 'Asia/Tokyo' },
  { label: 'Korea Standard Time',              value: 'Asia/Seoul' },
  { label: 'Cen. Australia Standard Time',     value: 'Australia/Adelaide' },
  { label: 'AUS Central Standard Time',        value: 'Australia/Darwin' },
  { label: 'E. Australia Standard Time',       value: 'Australia/Brisbane' },
  { label: 'AUS Eastern Standard Time',        value: 'Australia/Sydney' },
  { label: 'Tasmania Standard Time',           value: 'Australia/Hobart' },
  { label: 'Lord Howe Standard Time',          value: 'Australia/Lord_Howe' },
  { label: 'Central Pacific Standard Time',    value: 'Pacific/Guadalcanal' },
  { label: 'New Zealand Standard Time',        value: 'Pacific/Auckland' },
  { label: 'Chatham Islands Standard Time',    value: 'Pacific/Chatham' },
  { label: 'Tonga Standard Time',              value: 'Pacific/Tongatapu' },
  { label: 'Samoa Standard Time',              value: 'Pacific/Apia' },
  { label: 'Line Islands Standard Time',       value: 'Pacific/Kiritimati' },
];

/**
 * Map from Windows timezone display name → IANA timezone name.
 * Use this to convert a Windows timezone name received from the backend into
 * an IANA name that date-fns / TZDate can understand.
 */
export const WINDOWS_TO_IANA: Record<string, string> = Object.fromEntries(
  WINDOWS_TIMEZONE_OPTIONS.map(o => [o.label, o.value])
);

@Injectable({ providedIn: 'root' })
export class DateTimeSettingsState {
  private readonly _dateFormat = signal<string>(
    localStorage.getItem(DATE_FORMAT_KEY) ?? DEFAULT_DATE_FORMAT
  );

  private readonly _timezone = signal<string>(
    localStorage.getItem(TIMEZONE_KEY) ?? DEFAULT_TIMEZONE
  );

  /** Reactive read-only signal for the current date format string. */
  readonly dateFormat = this._dateFormat.asReadonly();

  /** Reactive read-only signal for the current timezone (IANA name or empty for browser local). */
  readonly timezone = this._timezone.asReadonly();

  /**
   * Sets the date format and persists it to localStorage.
   * @param format An Angular DatePipe format string (e.g. 'yyyy/MM/dd HH:mm:ss').
   */
  setDateFormat(format: string): void {
    localStorage.setItem(DATE_FORMAT_KEY, format);
    this._dateFormat.set(format);
  }

  /**
   * Sets the timezone and persists it to localStorage.
   * @param timezone An IANA timezone name (e.g. 'America/New_York') or empty string for browser local.
   */
  setTimezone(timezone: string): void {
    localStorage.setItem(TIMEZONE_KEY, timezone);
    this._timezone.set(timezone);
  }
}
